import React from 'react';
import { useLocation } from 'react-router-dom';

import { Modal as AntdModal } from 'antd';
import { filterUndefinedOrNull } from 'utils';

import {
  ComputedResponseDtoListActionsEnum,
  ComputedResponseDtoListAvailableFilesEnum,
} from 'lib_api/lib/api/gen';

import ActionAndFileWrapper from 'components/ActionAndFileWrapper/ActionAndFileWrapper';
import { AvailableAction } from 'components/ActionAndFileWrapper/types';
import CoordonneesProprietaireForm from 'components/FicheDescriptiveDisplay/CoordonneesProprietaire/CoordonneesProprietaireForm';
import ModalProlongation from 'components/FicheDescriptiveDisplay/Prolongation/ModalProlongation';
import ImporterPieceJointe from 'components/ImporterPieceJointe/ImporterPieceJointe';
import BaseModal from 'components/WrappedComponents/BaseModal';
import { useAFFilterContext } from 'hooks/AFFilterStoreContext';
import { useApi } from 'hooks/ApiStoreContext';
import { useDossierContext } from 'hooks/dossiers/DossierContext';
import { useModal } from 'hooks/modal/useModal';
import { useDisplayActionsRequisesBanner } from 'hooks/useActionsRequises';
import { useHandleBackErrors } from 'hooks/utils/handleBackErrors';
import { ActionsRequisesLinkState } from 'utils/actionsRequisesUtils';
import { BaseModalWidthUtils } from 'utils/BaseModalWidthUtils';
import { extractNonnullDossierIdOrThrow } from 'utils/dossierUtils';

import ControleBlocageVente from '../ControleBlocageVente/ControleBlocageVente';
import UpdateDonneesVehiculeAutoriteFourriere from '../UpdateDonneesVehicule/UpdateDonneesVehiculeAutoriteFourriere';
import UpdateEntiteRattachementAF from '../UpdateEntitesRattachements/UpdateEntiteRattachementAF';

const ActionsSuiviVehicule = (): React.ReactElement => {
  const behaviourOnError = useHandleBackErrors();
  const { state } = useLocation() as { state: ActionsRequisesLinkState };
  const [isProlongationOpen, setIsProlongationOpen] = React.useState<boolean>(
    state?.openProlongationMef || false,
  );
  const [isUpdateCoord, setIsUpdateCoord] = React.useState<boolean>(
    state?.openCoordonneesProprietaire || false,
  );
  const {
    visible: isControleBlocageVenteOpen,
    open: setIsControleBlocageVenteOpen,
    close: setIsControleBlocageVenteClosed,
  } = useModal(state?.openControleBlocageVenteManquant || false);

  const updateEntiteRattachementModal = useModal();
  const updateDonneesVehiculeModal = useModal();
  const importerPieceJointeModal = useModal();

  const [{ result: dossier, setDossier }] = useDossierContext();
  const [, , afActionsRequises] = useAFFilterContext();
  const dossierId = extractNonnullDossierIdOrThrow(dossier);

  // display the actions requises banner(s)
  useDisplayActionsRequisesBanner(dossier, afActionsRequises);

  const afController = useApi().AutoriteFourriereControllerApi;

  function mapComputedListActionsEnumToAvailableAction(
    actionEnum: ComputedResponseDtoListActionsEnum,
  ): AvailableAction | null {
    switch (actionEnum) {
      case ComputedResponseDtoListActionsEnum.PROLONGER_MEF:
        return {
          title: 'Prolonger la mise en fourrière administrative',
          onClick: () => {
            setIsProlongationOpen(true);
          },
        };

      case ComputedResponseDtoListActionsEnum.MODIFIER_COORDONNEES_PROPRIETAIRE:
        return {
          title: 'Modifier les coordonnées du propriétaire',
          onClick: () => {
            setIsUpdateCoord(true);
          },
        };

      case ComputedResponseDtoListActionsEnum.TRAITE_EN_FACTURATION:
        return {
          title: 'Marquer comme traité en facturation',
          onClick: () => {
            afController
              .submitTraitementFacturationUsingPOST(
                extractNonnullDossierIdOrThrow(dossier),
                true,
              )
              .then(setDossier)
              .catch(behaviourOnError);
          },
        };

      case ComputedResponseDtoListActionsEnum.NON_TRAITE_EN_FACTURATION:
        return {
          title: 'Marquer comme non traité en facturation',
          onClick: () => {
            afController
              .submitTraitementFacturationUsingPOST(
                extractNonnullDossierIdOrThrow(dossier),
                false,
              )
              .then(setDossier)
              .catch(behaviourOnError);
          },
        };

      case ComputedResponseDtoListActionsEnum.RELANCE_VENTE_MANQUANT:
        return {
          title: 'Contrôler le blocage de la vente',
          onClick: setIsControleBlocageVenteOpen,
        };

      case ComputedResponseDtoListActionsEnum.UPDATE_ENTITES_RATTACHEMENT_FO_AF:
        return {
          title: 'Modifier les entités de rattachement',
          onClick: updateEntiteRattachementModal.open,
        };
      case ComputedResponseDtoListActionsEnum.UPDATE_DONNEES_VEHICULE:
        return {
          title: "Modifier les données du véhicule et de l'infraction",
          onClick: updateDonneesVehiculeModal.open,
        };
      case ComputedResponseDtoListActionsEnum.IMPORTER_PIECES_JOINTES:
        return {
          title: 'Joindre un document',
          onClick: importerPieceJointeModal.open,
        };
      default:
        return null;
    }
  }

  const filesList: ComputedResponseDtoListAvailableFilesEnum[] =
    dossier.computed?.listAvailableFiles ?? [];
  const actions: AvailableAction[] = filterUndefinedOrNull(
    dossier.computed?.listActions?.map(action => {
      return mapComputedListActionsEnumToAvailableAction(action);
    }) || [],
  );

  return (
    <>
      <ActionAndFileWrapper
        filesList={filesList}
        piecesJointesList={dossier.computed?.listAvailablePiecesJointes ?? []}
        actions={actions}
        dossierId={dossierId}
      />

      {/* prolonger mise en fourriere modal */}
      <ModalProlongation
        visible={isProlongationOpen}
        close={() => {
          setIsProlongationOpen(false);
        }}
      />

      {/* update coordonnées propriétaire modal */}
      <AntdModal
        visible={isUpdateCoord}
        onCancel={() => setIsUpdateCoord(false)}
        footer={null}
        closable={false}
        destroyOnClose={true}
        width={BaseModalWidthUtils.smallWidth}
        maskClosable={false}
      >
        <CoordonneesProprietaireForm
          controller={afController}
          close={() => setIsUpdateCoord(false)}
          editable={true}
        />
      </AntdModal>
      {/* controle blocage vente modal */}
      <AntdModal
        visible={isControleBlocageVenteOpen}
        onCancel={setIsControleBlocageVenteClosed}
        footer={null}
        closable={true}
        destroyOnClose={true}
        width={BaseModalWidthUtils.mediumWidth}
        maskClosable={false}
      >
        <ControleBlocageVente close={setIsControleBlocageVenteClosed} />
      </AntdModal>
      <AntdModal
        visible={updateEntiteRattachementModal.visible}
        onCancel={updateEntiteRattachementModal.close}
        destroyOnClose={true}
        footer={null}
        width={BaseModalWidthUtils.mediumWidth}
        maskClosable={false}
      >
        <UpdateEntiteRattachementAF
          closeModal={updateEntiteRattachementModal.close}
        />
      </AntdModal>

      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateDonneesVehiculeModal.visible}
        closeModal={updateDonneesVehiculeModal.close}
      >
        <UpdateDonneesVehiculeAutoriteFourriere
          closeModal={updateDonneesVehiculeModal.close}
        />
      </BaseModal>

      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={importerPieceJointeModal.visible}
        closeModal={importerPieceJointeModal.close}
      >
        <ImporterPieceJointe closeModal={importerPieceJointeModal.close} />
      </BaseModal>
    </>
  );
};

export default ActionsSuiviVehicule;
